'use client'

import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { Button, Icon } from '@graftcode/design-system'

import { IMAGE_LIGHTBOX_TEST_IDS } from '@/shared/docs-test-ids.const'

import styles from './ImageLightbox.module.scss'

interface ImageLightboxProps {
  src: string
  alt: string
  onClose: () => void
}

export function ImageLightbox({ src, alt, onClose }: ImageLightboxProps) {
  const backdropRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  const handleBackdropClick = useCallback(
    (e: MouseEvent) => {
      if (e.target === backdropRef.current) onClose()
    },
    [onClose]
  )

  useEffect(() => {
    const backdrop = backdropRef.current
    document.addEventListener('keydown', handleKeyDown)
    backdrop?.addEventListener('click', handleBackdropClick)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      backdrop?.removeEventListener('click', handleBackdropClick)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown, handleBackdropClick])

  return createPortal(
    <div
      data-testid={IMAGE_LIGHTBOX_TEST_IDS.root}
      ref={backdropRef}
      className={styles.backdrop}
      aria-modal="true"
      role="dialog"
    >
      <Button
        className={styles.closeButton}
        iconOnly
        size="l"
        variant="ghost"
        icon={Icon.DismissRegular}
        onClick={onClose}
        aria-label="Close image"
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt={alt} className={styles.image} />
    </div>,
    document.body
  )
}
